import usePaging from 'new/state/paging/repository/usePaging';
import createFlowService from 'new/components/flows/service/createFlowService';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useFormData from 'new/state/forms/repository/useForms';
import useSinkSetup from 'new/components/flows/controller/useSinkSetup';
import useSourceSetup from 'new/components/flows/controller/useSourceSetup';
import useVendorsAdapter from 'new/state/vendors/adapter/useVendorsAdapter';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import { CONNECTION_CHANGE_RESET_FORM_FIELDS } from './connectionChangeResetFields';

type UseFlowsSetup = {
  data: any;
  initialLoading: boolean;
  effectiveConnectionType: any;
  start(): void;
  end(): void;
  cleanForm(id: number): void;
  setupInitialFormData(resources: any): void;
  updateFormData(data: any): void;
  cleanForm(id: any): void;
  setupInitialFormData({ sink, source, nexset }): void;
  resetFormData(data: string[]): void;
  /**
   * Used to select a table to a DB connector
   */
  onTableSelected(table: any, db: any): void;
  /**
   * Used to select connection.
   * - Creates a configuration based on the connectionType, whether is SOURCE or SINK
   * @param flowType string
   * @returns
   */
  onSelectConnection(flowType: string): (connection: string) => void;
  /**
   * It is a validation function that comes from useSourceSetup or useSinkSetup
   * Returns true if the validation is valid, and false if it doesn't
   * @param onError
   */
  validate(onError: (arg: string) => void): false | true;
  onBucketSelected(bucketConfig: any, selectedBucket: any): void;
  onSourceConfigChanged(config: any): void;
  onSinkConfigChanged(config: any, field: any, value: any): void;
};

/**
 * Starts the Form based on the form-type.
 * Creates the Flow Service that are util functions to handle the source/destination config.
 */
export default function useFlowsSetup(formType: string, flowType: string): UseFlowsSetup {
  const { getSourceConfigFields, getSinkConfigFields, getConnectionCategoryByConnectorType } =
    usePaging();

  const org = useCurrentOrg();
  const formData = useFormData(formType);
  const { requestFullVendor } = useVendorsAdapter();

  const { updateFormData, resetFormData, data = {} } = formData;

  const flowService = createFlowService();

  const { connectionType, selectedConnection, selectedCredentialInfo } = data || {};

  const sinkSetup = useSinkSetup({ ...formData, ...data }, flowType);
  const sourceSetup = useSourceSetup({ ...formData, ...data }, flowType);

  const resourceSetup = connectionType === 'SINK' ? sinkSetup : sourceSetup;

  function initializeSourceConfig(connection, flowType) {
    const sourceConfigFields = getSourceConfigFields(connection, flowType);
    const connectionCategory = getConnectionCategoryByConnectorType(connection);
    return flowService.createInitialSourceConfig({
      empty: true,
      sourceConfig: data.sourceConfig,
      sourceConfigFields,
      flowType,
      connectionCategory,
    });
  }

  function initializeSinkConfig(connection, flowType) {
    const sinkConfigFields = getSinkConfigFields(connection, flowType);
    return flowService.buildSinkConfig({
      newSinkConfig: {},
      sinkConfigFields,
      selectedConnection: connection,
      orgId: org.id,
      field: '',
      value: '',
      flowType,
    });
  }

  const onSelectConnection = (flowType) => (connection) => {
    const eventName =
      connectionType === 'SOURCE'
        ? 'data_source_connection_selected'
        : 'data_sink_connection_selected';
    AnalyticsUtils.captureCustomEvent(eventName, {
      selected_connector: connection,
    });

    requestFullVendor(connection);
    const sourceConfig =
      connectionType === 'SOURCE' ? initializeSourceConfig(connection, flowType) : undefined;
    const sinkConfig =
      connectionType === 'SINK' ? initializeSinkConfig(connection, flowType) : undefined;

    resetFormData(CONNECTION_CHANGE_RESET_FORM_FIELDS);
    updateFormData({
      sourceConfig,
      sinkConfig,
      connectionCategory: getConnectionCategoryByConnectorType(connection),
      selectedConnection: connection,
    });
  };

  const onSourceConfigChanged = (newSourceConfig) => {
    updateFormData({ sourceConfig: newSourceConfig });
  };

  const onSinkConfigChanged = (newSinkConfig, field, value) => {
    let sinkConfig = flowService.buildSinkConfig({
      newSinkConfig,
      flowType,
      sinkConfigFields: getSinkConfigFields(selectedConnection, flowType),
      field,
      value,
      selectedConnection,
      orgId: org.id,
      flowType,
    });

    updateFormData({ sinkConfig });
  };

  return {
    initialLoading: false,
    ...formData,
    data,
    ...resourceSetup,
    onSinkConfigChanged,
    onSourceConfigChanged,
    effectiveConnectionType: flowService.getConnectionType(
      selectedConnection,
      selectedCredentialInfo,
    ),
    onSelectConnection,
  };
}
